import React from 'react';
import { Button, Space, Typography } from 'antd';
import type { DeliveryGroupDraft, DeliveryGroupStopDraft } from './deliveryGroupTypes';
import { RouteGroupStopCard, type RouteStopCardContext } from './RouteGroupStopCard';
import { formatGroupTitle, ROUTE_UX } from './routeUxLabels';
import { nextStopPosition } from './editorStopOrdering';
import { isSequentialStopsEditorModeEnabled } from './sequentialStopsEditorFlags';
import { AvailableApplicationPicker } from '../routeCreatePhysicalSource/AvailableApplicationPicker';
import { PhysicalCargoSourcePicker } from '../routeCreatePhysicalSource/PhysicalCargoSourcePicker';
import {
  applyGroupApplicationChange,
  applyGroupPhysicalSourceSelection,
  getPrimaryLoadingStop
} from './groupPhysicalSourceFlow';

const { Text, Title } = Typography;

type ApplicationRef = {
  id: number;
  internalNumber?: string | null;
  client?: { name?: string | null } | null;
  consigneeId?: number | null;
  loadingAddress?: string | null;
  loadingContactPerson?: string | null;
  loadingPhone?: string | null;
  unloadingAddress?: string | null;
  unloadingContactPerson?: string | null;
  unloadingPhone?: string | null;
  consignee?: { name?: string | null; contactPerson?: string | null; phone?: string | null; address?: string | null } | null;
};

type Props = {
  group: DeliveryGroupDraft;
  allGroups?: DeliveryGroupDraft[];
  groupIndex: number;
  onChange: (next: DeliveryGroupDraft) => void;
  onRemove?: () => void;
  applications: ApplicationRef[];
  editingRouteId?: number;
  warehouses?: RouteStopCardContext['warehouses'];
  consignees?: RouteStopCardContext['consignees'];
  makeStopUid: () => string;
  inTransit?: boolean;
  allStops?: DeliveryGroupStopDraft[];
  resolveCityHint?: (address: string) => Promise<string | null>;
  normalizeCity?: (city: string | null | undefined) => string | null;
  onGroupExecutionScopeClear?: (groupUid: string) => void;
};

function assignPositionOnNewStop(
  draft: DeliveryGroupStopDraft,
  allGroups: DeliveryGroupDraft[] | undefined
): DeliveryGroupStopDraft {
  if (!isSequentialStopsEditorModeEnabled()) return draft;
  const groups = allGroups ?? [];
  return { ...draft, position: nextStopPosition(groups) };
}

export function RouteGroupCard({
  group,
  allGroups,
  groupIndex,
  onChange,
  onRemove,
  applications,
  editingRouteId,
  warehouses,
  consignees,
  makeStopUid,
  inTransit,
  allStops,
  resolveCityHint,
  normalizeCity,
  onGroupExecutionScopeClear
}: Props) {
  const app = group.applicationId ? applications.find((a) => a.id === group.applicationId) : undefined;
  const groupStops = [...group.loadingStops, ...group.unloadingStops];
  const groupHasCompletedStop = groupStops.some((s) => s.lockState === 'COMPLETED');
  const groupApplicationLocked = Boolean(inTransit && groupHasCompletedStop);

  const primaryLoading = getPrimaryLoadingStop(group);

  const applyApplicationToGroup = (applicationId: number) => {
    onGroupExecutionScopeClear?.(group.uid);
    const selected = applications.find((a) => a.id === applicationId);
    onChange(
      applyGroupApplicationChange({
        group,
        applicationId,
        application: selected,
        makeStopUid
      })
    );
  };

  const applyPhysicalSource = (patch: {
    loadingPointType: 'application' | 'warehouse';
    loadingPointId: number;
    applicationId: number;
  }) => {
    onGroupExecutionScopeClear?.(group.uid);
    const selectedApp = applications.find((a) => a.id === patch.applicationId);
    const warehouse =
      patch.loadingPointType === 'warehouse'
        ? warehouses?.find((w) => w.id === patch.loadingPointId)
        : undefined;
    onChange(
      applyGroupPhysicalSourceSelection({
        group,
        loadingPointType: patch.loadingPointType,
        loadingPointId: patch.loadingPointId,
        applicationId: patch.applicationId,
        application: selectedApp,
        warehouse,
        makeStopUid
      })
    );
  };

  const stopCtx: RouteStopCardContext = {
    warehouses,
    consignees,
    application: app ?? null,
    groupApplicationId: group.applicationId ?? null,
    applicationConsigneeId: app?.consigneeId ?? null,
    inTransit,
    allStops,
    resolveCityHint,
    normalizeCity
  };

  const title = formatGroupTitle({
    applicationNumber: app?.internalNumber ?? null,
    clientName: app?.client?.name ?? null,
    index: groupIndex
  });

  const extraLoadingStops = group.loadingStops.slice(1);

  return (
    <div
      style={{
        border: '1px solid #d9d9d9',
        borderRadius: 10,
        padding: 16,
        marginBottom: 16,
        background: '#fafcff'
      }}
    >
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 12 }}>
        <Title level={5} style={{ margin: 0 }}>
          {title}
        </Title>
        {onRemove ? (
          <Button type="link" danger disabled={groupHasCompletedStop} onClick={onRemove}>
            {ROUTE_UX.removeGroup}
          </Button>
        ) : null}
      </Space>

      <Space direction="vertical" style={{ width: '100%', marginBottom: 16 }}>
        <Text type="secondary">Заявка (CP-059 selectable)</Text>
        <AvailableApplicationPicker
          value={group.applicationId}
          excludeRouteId={editingRouteId}
          disabled={groupApplicationLocked}
          onChange={applyApplicationToGroup}
        />
      </Space>

      {group.applicationId ? (
        <Space direction="vertical" style={{ width: '100%', marginBottom: 16 }}>
          <Text type="secondary">Физический источник груза</Text>
          <PhysicalCargoSourcePicker
            applicationId={group.applicationId}
            loadingPointType={primaryLoading?.pointType}
            loadingPointId={primaryLoading?.pointId}
            excludeRouteId={editingRouteId}
            disabled={groupApplicationLocked}
            onSourceSelected={applyPhysicalSource}
          />
        </Space>
      ) : null}

      <Text strong style={{ display: 'block', marginBottom: 8 }}>
        {ROUTE_UX.loadingSection}
      </Text>
      {primaryLoading && primaryLoading.pointType && primaryLoading.pointId ? (
        <RouteGroupStopCard
          key={primaryLoading.uid}
          stop={primaryLoading}
          stopType="loading"
          index={0}
          allowRemove={false}
          ctx={{ ...stopCtx, groupApplicationId: group.applicationId }}
          onChange={(next) =>
            onChange({
              ...group,
              loadingStops: [next, ...extraLoadingStops]
            })
          }
          onRemove={() => undefined}
        />
      ) : (
        <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
          Выберите физический источник выше — адрес погрузки подставится автоматически.
        </Text>
      )}

      {extraLoadingStops.map((s, idx) => (
        <RouteGroupStopCard
          key={s.uid}
          stop={s}
          stopType="loading"
          index={idx + 1}
          ctx={stopCtx}
          onChange={(next) =>
            onChange({
              ...group,
              loadingStops: [group.loadingStops[0], ...extraLoadingStops].map((row) =>
                row.uid === s.uid ? next : row
              )
            })
          }
          onRemove={() =>
            onChange({
              ...group,
              loadingStops: group.loadingStops.filter((row) => row.uid !== s.uid)
            })
          }
        />
      ))}
      <Button
        type="dashed"
        size="small"
        style={{ marginBottom: 16 }}
        onClick={() =>
          onChange({
            ...group,
            loadingStops: [
              ...group.loadingStops,
              assignPositionOnNewStop(
                {
                  uid: makeStopUid(),
                  groupUid: group.uid,
                  type: 'loading' as const,
                  pointType: group.applicationId ? 'application' : 'warehouse',
                  pointId: group.applicationId ?? null,
                  address: app?.loadingAddress ?? null,
                  contactPerson: app?.loadingContactPerson ?? null,
                  phone: app?.loadingPhone ?? null,
                  plannedAt: null,
                  actualAt: null,
                  notes: null,
                  status: null
                },
                allGroups
              )
            ]
          })
        }
      >
        + {ROUTE_UX.addLoadingStop}
      </Button>

      <Text strong style={{ display: 'block', marginBottom: 8 }}>
        {ROUTE_UX.unloadingSection}
      </Text>
      {group.unloadingStops.map((s, idx) => (
        <RouteGroupStopCard
          key={s.uid}
          stop={s}
          stopType="unloading"
          index={idx}
          ctx={stopCtx}
          onChange={(next) =>
            onChange({
              ...group,
              unloadingStops: group.unloadingStops.map((row) => (row.uid === s.uid ? next : row))
            })
          }
          onRemove={() =>
            onChange({ ...group, unloadingStops: group.unloadingStops.filter((row) => row.uid !== s.uid) })
          }
        />
      ))}
      <Button
        type="dashed"
        size="small"
        onClick={() =>
          onChange({
            ...group,
            unloadingStops: [
              ...group.unloadingStops,
              assignPositionOnNewStop(
                {
                  uid: makeStopUid(),
                  groupUid: group.uid,
                  type: 'unloading' as const,
                  pointType: 'application',
                  pointId: group.applicationId ?? app?.id ?? null,
                  address: app?.unloadingAddress ?? app?.consignee?.address ?? null,
                  contactPerson: app?.unloadingContactPerson ?? app?.consignee?.contactPerson ?? null,
                  phone: app?.unloadingPhone ?? app?.consignee?.phone ?? null,
                  plannedAt: null,
                  actualAt: null,
                  notes: null,
                  status: null
                },
                allGroups
              )
            ]
          })
        }
      >
        + {ROUTE_UX.addUnloadingStop}
      </Button>
    </div>
  );
}
